import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { tagStadium, TagWithDetails, ClubSettings, SurveyWithQuestions, Event, CtaButton, EventLineup, Prize, MenuItem, CollectionPoint, SectionOrderingMode, PaymentMode, Poll, PollResultRow, FanOperator, OperatorSettings } from '@/lib/supabase-types';

interface FanPageData {
  tag: TagWithDetails | null;
  clubId: string | null;
  clubName: string | null;
  clubSlug: string | null;
  event: Event | null;
  /** The tenant's settings with the serving kiosk's ordering switches overlaid (old clients keep working). */
  settings: ClubSettings | null;
  /** The kiosk serving this seat, and whether the tenant picked it or it is the venue's default. null = nobody. */
  operator: FanOperator | null;
  /** That kiosk's ordering switches, unoverlaid, so the page can follow them live. */
  ordering: OperatorSettings | null;
  ctaButtons: CtaButton[];
  survey: SurveyWithQuestions | null;
  polls: Poll[];
  pollResults: PollResultRow[];
  lineups: EventLineup[];
  prizes: Prize[];
  predictorPrizes: Prize[];
  surveyPrizes: Prize[];
  menuItems: MenuItem[];
  menuCategories: any[];
  orderingMode: SectionOrderingMode;
  collectionPoint: CollectionPoint | null;
  paymentMode: PaymentMode;
}

interface UseFanPageDataResult extends FanPageData {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  stadiumSlugMismatch: boolean;
  stadiumSlug: string | null;
}

// Type for tag with stadium clubs (includes stadium slug) — under the seat for
// a seat tag, on the tag itself for a screen tag.
type StadiumWithClubs = {
  id: string;
  slug: string;
  stadium_clubs: {
    club_id: string;
    clubs?: { id: string; name: string; slug?: string } | { id: string; name: string; slug?: string }[]
  }[];
};
type TagWithStadiumClubs = TagWithDetails & {
  seat: { stadium: StadiumWithClubs } | null;
  stadium?: StadiumWithClubs | null;
};

export function useFanPageData(tagPublicId: string | undefined, expectedStadiumSlug?: string, clubId?: string): UseFanPageDataResult {
  const fanPageQuery = useQuery({
    queryKey: ['fan-page-data', tagPublicId, clubId],
    queryFn: async () => {
      // In preview mode, clubId is provided but tagPublicId might be missing or invalid
      if (!tagPublicId && !clubId) return null;

      const { data, error } = await supabase
        .rpc('get_fan_page_data' as any, {
          _tag_public_id: tagPublicId || 'PREVIEW',
          _club_id: clubId || null
        });

      if (error) throw error;
      return data as any;
    },
    enabled: !!tagPublicId || !!clubId,
    staleTime: 0,
    gcTime: 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep previous data during re-fetches (v5 pattern)
  });

  const rawData = fanPageQuery.data;

  // Stabilize the derived data to prevent infinite re-render loops in components that depend on these arrays/objects
  return useMemo(() => {
    // Extract and map data from the RPC JSON response
    const tagData = rawData?.tag || null;
    const event = rawData?.event || null;
    const clubId = rawData?.club_id || null;
    const settings = rawData?.settings || null;
    const operator: FanOperator | null = rawData?.operator ?? null;
    const ordering: OperatorSettings | null = rawData?.ordering ?? null;
    const ctaButtons = rawData?.cta_buttons || [];
    const survey = rawData?.survey || null;
    const polls = rawData?.polls || [];
    const pollResults = rawData?.poll_results || [];
    const predictorPrizes = rawData?.predictor_prizes || [];
    const surveyPrizes = rawData?.survey_prizes || [];
    const lineups = rawData?.lineups || [];
    const menuItems = rawData?.menu_items || [];
    const menuCategories = rawData?.menu_categories || [];
    const orderingMode: SectionOrderingMode = rawData?.ordering_mode || 'delivery';
    const collectionPoint: CollectionPoint | null = rawData?.collection_point || null;
    const paymentMode: PaymentMode = rawData?.payment_mode || settings?.payment_mode || 'in_person_only';

    // Get stadium slug from tag data (screen tags carry the venue themselves)
    const venue = tagStadium(tagData as TagWithDetails | null) as unknown as StadiumWithClubs | null;
    const stadiumSlug = venue?.slug || null;

    // derivation of club metadata
    const stadiumClubs = venue?.stadium_clubs;
    const firstStadiumClub = stadiumClubs?.[0];
    const clubsData = firstStadiumClub?.clubs;
    const fallbackClubData = Array.isArray(clubsData) ? clubsData[0] : clubsData;
    const fallbackClubName = fallbackClubData?.name || null;

    // Validate stadium slug if provided
    const stadiumSlugMismatch = !!(expectedStadiumSlug && stadiumSlug && expectedStadiumSlug !== stadiumSlug);

    // Ultimate safety catch: if somehow derived data is missing or null, return safe fallbacks
    return {
      tag: (tagData as TagWithDetails) || null,
      clubId: clubId || null,
      // The resolved team (a screen tag that follows the stadium shows whoever
      // plays); older payloads without club_name fall back to the venue's first tenant.
      clubName: (rawData?.club_name as string | undefined) || fallbackClubName || null,
      clubSlug: null,
      event: (event as Event) || null,
      settings: (settings as ClubSettings) || null,
      operator,
      ordering,
      ctaButtons: Array.isArray(ctaButtons) ? ctaButtons : [],
      survey: (survey as unknown as SurveyWithQuestions) || null,
      polls: Array.isArray(polls) ? (polls as unknown as Poll[]) : [],
      pollResults: Array.isArray(pollResults) ? (pollResults as PollResultRow[]) : [],
      lineups: Array.isArray(lineups) ? lineups : [],
      prizes: [
        ...(Array.isArray(predictorPrizes) ? predictorPrizes : []),
        ...(Array.isArray(surveyPrizes) ? surveyPrizes : [])
      ],
      predictorPrizes: Array.isArray(predictorPrizes) ? predictorPrizes : [],
      surveyPrizes: Array.isArray(surveyPrizes) ? surveyPrizes : [],
      menuItems: Array.isArray(menuItems) ? menuItems : [],
      menuCategories: Array.isArray(menuCategories) ? menuCategories : [],
      orderingMode,
      collectionPoint,
      paymentMode,
      isLoading: !!fanPageQuery.isLoading,
      isError: !!fanPageQuery.isError,
      error: (fanPageQuery.error as Error) || null,
      stadiumSlugMismatch,
      stadiumSlug,
    };
  }, [rawData, expectedStadiumSlug, fanPageQuery.isLoading, fanPageQuery.isError, fanPageQuery.error]);
}
